import { useState, useRef, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getNavigation, getAiStatus } from '../api/client.js';
import { useSession } from '../context/SessionContext.jsx';

/**
 * The nav.
 *
 * Replaces server/buildNavs.js, which concatenated ~250 lines of Bootstrap
 * markup from profile data with no escaping. Here the API returns structured
 * items and React escapes every label by construction.
 *
 * Collapsed, it becomes a 64px icon rail; hovering or focusing an item opens
 * a flyout with the label and, for a group, its children — so nothing becomes
 * unreachable when the labels are hidden.
 */
/**
 * Whether the "Off" assistant entry explains itself in the sidebar.
 *
 * Off for now: three lines of prose under a menu item costs navigation space
 * on every screen, for a state most people will never be in.
 *
 * The cost of hiding it is real and worth stating. Somebody who sees a greyed
 * entry marked "Off" and no reason has nowhere to go, and the reason is not
 * available any other way in the menu - there is no tooltip, deliberately,
 * because a tooltip only exists for people using a mouse. What they still
 * have is the assistant page itself: clicking through is blocked, but an
 * administrator visiting /assistant gets the full blocker list, and everyone
 * else gets the same sentence this would have shown.
 *
 * Flip to true to bring it back; the markup and CSS are intact.
 */
const SHOW_AI_OFF_REASON = false;

export default function Sidebar({ surface = 'Desktop', collapsed, onToggleCollapse,
                                  isOpen, onNavigate }) {
  const { user, customer, theme, isPlatformAdmin } = useSession();
  // A demo session is refused admin server-side; hiding those links here only
  // avoids offering something that would 403. The assistant is different: a
  // demo may use it under an allowance, so the link is shown and the limit is
  // enforced on the server.
  const isDemo = !!user?.isDemo;
  const [flyout, setFlyout] = useState(null);
  const closeTimer = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ['navigation', surface],
    queryFn: () => getNavigation(surface)
  });

  // Gated on the per-customer flag: the AI backend 403s tenants it does not
  // know, so an always-visible entry would be a dead link for most customers.
  const { data: ai } = useQuery({
    queryKey: ['ai', 'status'], queryFn: getAiStatus, retry: false, staleTime: 5 * 60_000
  });

  /** Position the flyout from the hovered element's box (the nav scrolls). */
  const openFlyout = useCallback((event, payload) => {
    if (!collapsed) return;
    clearTimeout(closeTimer.current);
    const rect = event.currentTarget.getBoundingClientRect();
    setFlyout({ ...payload, top: rect.top });
  }, [collapsed]);

  // A small delay so the pointer can cross the gap into the panel.
  const scheduleClose = useCallback(() => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setFlyout(null), 120);
  }, []);
  const cancelClose = useCallback(() => clearTimeout(closeTimer.current), []);

  const dismiss = useCallback(() => {
    setFlyout(null);
    onNavigate?.();
  }, [onNavigate]);

  const classes = ['side-nav'];
  if (collapsed) classes.push('is-collapsed');
  if (isOpen) classes.push('is-open');

  if (isLoading) {
    return <nav className={classes.join(' ')}><SidebarSkeleton /></nav>;
  }
  if (!data) return null;

  const hasContent = data.favourites.length > 0 || data.groups.length > 0;
  // The sidebar follows the theme, so show the logo drawn for that
  // background. Each falls back to the other in the API, so one configured
  // logo still appears on both.
  const logo = (theme === 'dark' ? customer?.logos?.dark : customer?.logos?.light) ?? null;

  const itemProps = (payload) => ({
    onMouseEnter: (e) => openFlyout(e, payload),
    onMouseLeave: scheduleClose,
    onFocus: (e) => openFlyout(e, payload),
    onBlur: scheduleClose
  });

  return (
    <>
      <nav className={classes.join(' ')} onMouseLeave={scheduleClose}>
        <div className="side-nav-brand">
          {logo
            ? <img src={logo} alt={customer?.name ?? 'Logo'} />
            : <span className="side-nav-brand-text">{customer?.name ?? 'Vizora'}</span>}
          {/* One button in one place: collapsed or expanded, the control
              stays in the brand row and only its icon changes. */}
          <button type="button" className="side-nav-collapse"
                  onClick={onToggleCollapse}
                  aria-expanded={!collapsed}
                  title={collapsed ? 'Expand menu' : 'Collapse menu'}
                  aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}>
            <i className={`bi ${collapsed ? 'bi-chevron-double-right' : 'bi-chevron-double-left'}`} />
          </button>
        </div>

        {/* Pinned above Favourites — the one entry that is always first,
            regardless of profile.

            Admins see it even when it is not live, marked "Off", so there is
            a way in to find out why. Hiding it entirely left no route to the
            explanation, which is how "it just isn't showing" happens. */}
        {/* Demo visitors see it too now: it is the most persuasive thing in
            the product, and the allowance is enforced server-side rather than
            by hiding the link. Admin screens stay hidden from them. */}
        {/* Always present, on or off.

            Off, it is a plain element rather than a link - a NavLink that
            routes to a page saying "not enabled" is a worse answer than a
            control that visibly will not move. The title carries the reason
            from /ai/status, so hovering says which switch is off instead of
            leaving somebody to guess between three of them.

            Shown to everyone, not only administrators. Three switches now
            govern this (customer, profile, user) and a user who cannot see
            the entry at all has no way to know there is something to ask for. */}
        <div className="side-nav-ai" {...itemProps({ label: 'Vizora AI Data Assistant' })}>
          {ai?.enabled ? (
            <NavLink className="side-nav-link" to="/assistant" onClick={dismiss}>
              <i className="bi bi-stars" />
              <span>Vizora AI Data Assistant</span>
            </NavLink>
          ) : (
            <div className="side-nav-ai-disabled">
              {/* Not a link and not a button: there is nothing to press. */}
              <div className="side-nav-link is-off">
                <i className="bi bi-stars" />
                <span>Vizora AI Data Assistant</span>
                <span className="side-nav-ai-off">Off</span>
              </div>
              {SHOW_AI_OFF_REASON && (
                <p className="side-nav-ai-reason">
                  {ai?.message ?? 'Not enabled for your account. '
                    + 'Your portal administrator can turn it on.'}
                </p>
              )}
            </div>
          )}
        </div>

        {data.favourites.length > 0 && (
          <>
            <div className="side-nav-title">Favourites</div>
            <ul className="list-unstyled mb-0">
              {data.favourites.map(item => (
                <li key={`fav-${item.id}`} {...itemProps({ label: item.label })}>
                  <NavItem item={item} icon="bi-star-fill" onDismiss={dismiss} />
                </li>
              ))}
            </ul>
          </>
        )}

        {groupBySection(data.groups).map(([section, groups]) => (
          <div key={section}>
            <div className="side-nav-title">{section}</div>
            <ul className="list-unstyled mb-0">
              {groups.map((group, i) => (
                <NavGroup key={`${section}-${i}`} group={group} onDismiss={dismiss}
                          collapsed={collapsed} itemProps={itemProps} />
              ))}
            </ul>
          </div>
        ))}

        {!hasContent && !collapsed && (
          <div className="p-3 text-muted small">
            {user?.isAdmin
              ? <>Nothing is assigned to your profile yet. Sync your workspace under{' '}
                <NavLink to="/admin/catalogue">Power BI Content</NavLink>, then add
                reports to a profile.</>
              : <>No content has been assigned to your profile yet. Contact your
                administrator if you think this is wrong.</>}
          </div>
        )}

        {!isDemo && user?.isAdmin && (
          <>
            <div className="side-nav-title">Admin</div>
            <ul className="list-unstyled mb-0">
              {ADMIN_LINKS.map(link => (
                <li key={link.to} {...itemProps({ label: link.label })}>
                  <NavLink className="side-nav-link" to={link.to} onClick={dismiss}>
                    <i className={`bi ${link.icon}`} /> <span>{link.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Vizora staff only — never rendered for a customer, and the
            server 404s the routes regardless. */}
        {isPlatformAdmin && (
          <>
            <div className="side-nav-title">Vizora</div>
            <ul className="list-unstyled mb-0">
              <li {...itemProps({ label: 'Customers' })}>
                <NavLink className="side-nav-link side-nav-staff" to="/platform/customers"
                         onClick={dismiss}>
                  <i className="bi bi-buildings-fill" /> <span>Customers</span>
                </NavLink>
              </li>
            </ul>
          </>
        )}
      </nav>

      {collapsed && flyout && (
        <div className="nav-flyout"
             style={{ top: flyout.top, left: 'var(--side-nav-collapsed-width)' }}
             onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
          {flyout.items?.length
            ? (
              <>
                <div className="nav-flyout-title">{flyout.label}</div>
                <ul className="nav-flyout-items">
                  {flyout.items.map(item => (
                    <li key={item.id}>
                      <FlyoutItem item={item} onDismiss={dismiss} />
                    </li>
                  ))}
                </ul>
              </>
            )
            : <div className="nav-flyout-title">{flyout.label}</div>}
        </div>
      )}
    </>
  );
}

const ADMIN_LINKS = [
  { to: '/admin/users',     icon: 'bi-person-fill-gear',  label: 'User Setup' },
  { to: '/admin/profiles',  icon: 'bi-gear-fill',         label: 'Profile Management' },
  { to: '/admin/catalogue', icon: 'bi-collection-fill',   label: 'Power BI Content' },
  { to: '/admin/paginated', icon: 'bi-printer-fill',      label: 'Pagination Setup' },
  { to: '/admin/ai-spend',  icon: 'bi-cash-coin',         label: 'Assistant Spend' },
  { to: '/admin/usage',     icon: 'bi-graph-up',          label: 'Report Usage' },
  { to: '/admin/audit',     icon: 'bi-journal-text',      label: 'Audit Log' }
];

function NavGroup({ group, onDismiss, collapsed, itemProps }) {
  const [open, setOpen] = useState(false);

  // A group whose items are all unlabelled renders as plain links.
  if (!group.collapsible) {
    return (
      <>
        {group.items.map(item => (
          <li key={item.id} {...itemProps({ label: item.label })}>
            <NavItem item={item} onDismiss={onDismiss} />
          </li>
        ))}
      </>
    );
  }

  // Collapsed, the whole group is one icon and its children live in the flyout.
  if (collapsed) {
    return (
      <li {...itemProps({ label: group.label, items: group.items })}>
        <button type="button" className="side-nav-link">
          <i className="bi bi-folder-fill" />
          <span>{group.label}</span>
        </button>
      </li>
    );
  }

  return (
    <li>
      <button type="button" className="side-nav-link side-nav-toggle"
              aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <i className="bi bi-folder-fill" />
        <span>{group.label}</span>
        <i className={`bi ms-auto ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
      </button>
      {open && (
        <ul className="side-nav-second-level list-unstyled mb-0">
          {group.items.map(item => (
            <li key={item.id}><NavItem item={item} onDismiss={onDismiss} /></li>
          ))}
        </ul>
      )}
    </li>
  );
}

/** Where a nav item points, shared by the rail and the flyout. */
function targetOf(item) {
  if (item.itemType === 'Website') return `/site/${item.id}`;
  return {
    Report: `/report/${item.targetId}`,
    PaginatedReport: `/paginated/${item.targetId}`,
    Dashboard: `/dashboard/${item.targetId}`
  }[item.itemType];
}

function iconOf(item) {
  if (item.itemType === 'Website') {
    return item.openInNewTab ? 'bi-box-arrow-up-right' : 'bi-globe';
  }
  return item.itemType === 'PaginatedReport' ? 'bi-printer-fill' : 'bi-graph-up';
}

function NavItem({ item, icon, onDismiss }) {
  const label = item.label ?? '(untitled)';

  if (item.itemType === 'Website' && item.openInNewTab) {
    // noopener/noreferrer: the old app's target="_blank" links gave the
    // opened page a handle on window.opener.
    return (
      <a className="side-nav-link" href={item.targetUrl}
         target="_blank" rel="noopener noreferrer">
        <i className={`bi ${icon ?? iconOf(item)}`} /> <span>{label}</span>
      </a>
    );
  }

  // NavLink handles the navigation itself; onClick only dismisses the
  // flyout and the mobile drawer. Websites hosted in an iframe go through
  // here too — they used to be plain <button>s, which is why they never
  // picked up the active highlight that reports get.
  return (
    <NavLink className="side-nav-link" to={targetOf(item)} onClick={onDismiss}>
      <i className={`bi ${icon ?? iconOf(item)}`} /> <span>{label}</span>
      {item.itemType === 'PaginatedReport' &&
        <span className="badge bg-info rounded-pill ms-2">Print</span>}
    </NavLink>
  );
}

function FlyoutItem({ item, onDismiss }) {
  const label = item.label ?? '(untitled)';

  if (item.itemType === 'Website' && item.openInNewTab) {
    return (
      <a href={item.targetUrl} target="_blank" rel="noopener noreferrer">
        <i className={`bi ${iconOf(item)}`} /> {label}
      </a>
    );
  }
  return (
    <NavLink to={targetOf(item)} onClick={onDismiss}>
      <i className={`bi ${iconOf(item)}`} /> {label}
    </NavLink>
  );
}

function groupBySection(groups) {
  const map = new Map();
  for (const g of groups) {
    if (!map.has(g.section)) map.set(g.section, []);
    map.get(g.section).push(g);
  }
  return [...map.entries()];
}

function SidebarSkeleton() {
  return (
    <div className="p-3" aria-hidden="true">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="placeholder-glow mb-2">
          <span className="placeholder col-10" />
        </div>
      ))}
    </div>
  );
}
